/**
 * Route-level lock: every navigation withholds autopilot function responses until the destination
 * page has published context. Pages claim via `useAutopilotRouteReady`; unclaimed pages unlock on
 * settle. Acquire the new lock before releasing the old one so withheld responses cannot flush
 * between hops.
 */

type Release = () => void;

/**
 * Opaque handle identifying one navigation's lock. `beginRouteTransition` returns it and the
 * router plugin threads it into `settleRouteTransition`/`abortRouteTransition`, so a settle or
 * abort delivered late (vue-router reports a CANCELLED navigation's failure only after the
 * navigation that cancelled it has already acquired the new lock) can never touch a newer
 * transition — it just no-ops.
 */
export type RouteTransitionToken = { readonly label: string };

interface RouteTransition {
  /** the navigation this lock belongs to — used only for log messages */
  label: string;
  /** idempotent release of the underlying client context resolution */
  release: Release;
  /** outstanding page claims; a settled transition unlocks once this drains to zero */
  claims: number;
  /** the navigation has finished rendering — no further claims are accepted */
  settled: boolean;
}

let current: RouteTransition | null = null;

// waiters for waitForRouteReady() — resolved when the current transition finishes
let finishListeners: Array<() => void> = [];

// optional debug logger; no-op until the app injects one via setRouteReadinessLogger
let log: (message: string) => void = () => {};

/** Inject a logger for the readiness lifecycle (the router plugin points this at the panel). */
export function setRouteReadinessLogger(logger: (message: string) => void): void {
  log = logger;
}

function notifyRouteReady(): void {
  const listeners = finishListeners;
  finishListeners = [];
  for (const listener of listeners) listener();
}

function finish(transition: RouteTransition): void {
  transition.release();
  if (current === transition) current = null;
  log(`route readiness resolved — ${transition.label} (function responses released)`);
  notifyRouteReady();
}

/**
 * Resolves when the in-flight route-readiness lock is fully released (page claimed + ready, or
 * unclaimed settle). Navigating autopilot functions must await this after `router.push` so their
 * handler does not return — and the assistant does not see the result — until the destination
 * page has published context and registered its tools.
 *
 * Resolves immediately when no transition is in flight. A 10s timeout matches the client's
 * contextResolutionTimeoutMs so a page that never calls ready() cannot hang the conversation.
 */
export function waitForRouteReady(): Promise<void> {
  if (!current) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      finishListeners = finishListeners.filter((l) => l !== onFinish);
      resolve();
    }, 10_000);
    const onFinish = () => {
      clearTimeout(timer);
      resolve();
    };
    finishListeners.push(onFinish);
  });
}

/**
 * Starts the lock for a new navigation, superseding (and releasing) any previous one. Returns the
 * token that scopes this navigation's later `settleRouteTransition`/`abortRouteTransition` calls.
 */
export function beginRouteTransition(acquire: (label: string) => Release, label: string): RouteTransitionToken {
  // acquire the new resolution BEFORE releasing the superseded one: the client resolves its
  // response waiters synchronously the moment the resolution set drains to zero, so a
  // release-then-acquire would let withheld function responses escape in between (e.g. on the
  // /jump/* redirect hop). The two locks must overlap.
  const previous = current;
  current = { label, release: acquire(label), claims: 0, settled: false };
  if (previous) {
    previous.release();
    log(`route readiness superseded — ${previous.label}`);
  }
  log(`route readiness acquired — ${label}`);
  return current;
}

/**
 * Marks the navigation as rendered. Called by the router plugin once the destination page has
 * mounted (and thus had its chance to claim). Unclaimed transitions unlock right here; claimed
 * ones unlock when the last `ready()` lands. Ignored when the token's navigation has already been
 * superseded — the two ticks between afterEach and settle leave room for a newer navigation to
 * begin (e.g. a redirect page's replace), and its lock must not be settled by the old navigation.
 */
export function settleRouteTransition(token: RouteTransitionToken): void {
  if (!current || current !== token) return;
  current.settled = true;
  if (current.claims === 0) finish(current);
  else log(`route readiness settled, awaiting ${current.claims} claim(s) — ${current.label}`);
}

/**
 * Releases the lock of a failed or cancelled navigation immediately. Ignored when the token's
 * navigation has already been superseded: vue-router delivers a cancelled navigation's failure
 * AFTER the cancelling navigation has acquired the new lock, so an unscoped abort here would
 * drain the newer lock and let withheld function responses escape mid-transition.
 */
export function abortRouteTransition(token: RouteTransitionToken): void {
  if (!current || current !== token) {
    log(`route readiness abort ignored — ${token.label} already superseded`);
    return;
  }
  log(`route readiness aborted — ${current.label}`);
  finish(current);
}

/**
 * Claims the in-flight navigation: the route lock is held until the returned `ready()` is called
 * (idempotent — safe to call from both a data watcher and unmount). A page that already claimed
 * can have v-if children join the lock even after settle; once the transition has finished
 * (or never started) this is a no-op — e.g. a tab switch after the page is already ready.
 */
export function claimRouteReadiness(): Release {
  const transition = current;
  if (!transition) return () => {};
  transition.claims += 1;
  log(`route readiness claimed (${transition.claims} pending) — ${transition.label}`);
  let done = false;
  return () => {
    if (done) return;
    done = true;
    transition.claims -= 1;
    log(`route readiness claim ready (${transition.claims} pending) — ${transition.label}`);
    if (transition.settled && transition.claims === 0) finish(transition);
  };
}
